import React, { useEffect, useState } from 'react';
import { Drawer, Descriptions, Tag, Button, Space, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { WaveRuleDTO } from '../../../../types/wave/waveRule';
import * as waveRuleService from '../../../../services/wave/waveRuleService';
import { WaveRuleStatusOptions } from '../../../../constants/wave';

interface WaveRuleDetailProps {
  visible: boolean;
  ruleCode: string;
  onClose: () => void;
  onEdit?: (ruleCode: string) => void;
}

const WaveRuleDetail: React.FC<WaveRuleDetailProps> = ({ visible, ruleCode, onClose, onEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<WaveRuleDTO | null>(null);

  useEffect(() => {
    if (visible && ruleCode) {
      loadDetail();
    }
  }, [visible, ruleCode]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const response = await waveRuleService.getWaveRuleByCode(ruleCode);
      if (response.success && response.data) {
        setDetail(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY') : '-';
  };

  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: string) => {
    const option = WaveRuleStatusOptions.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  if (!detail) {
    return (
      <Drawer
        title={t('wave.waveRule.detail.title')}
        width={720}
        open={visible}
        onClose={onClose}
      >
        <Spin spinning={loading} />
      </Drawer>
    );
  }

  return (
    <Drawer
      title={t('wave.waveRule.detail.title')}
      width="50vw"
      open={visible}
      onClose={onClose}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>{t('common.close')}</Button>
            {onEdit && (
              <Button type="primary" onClick={() => onEdit(ruleCode)}>
                {t('common.edit')}
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* Basic Information */}
        <Descriptions title={t('wave.waveRule.detail.basicInfo.title')} column={2} bordered>
          <Descriptions.Item label={t('wave.waveRule.detail.basicInfo.ruleCode')}>
            {detail.ruleCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.basicInfo.ruleName')}>
            {detail.ruleName}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.basicInfo.status')}>
            {renderStatusTag(detail.status)}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.basicInfo.effectiveStartDate')}>
            {formatDate(detail.effectiveStartDate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.basicInfo.effectiveEndDate')} span={2}>
            {detail.effectiveEndDate ? formatDate(detail.effectiveEndDate) : t('common.label.permanent')}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.basicInfo.description')} span={2}>
            {detail.description || '-'}
          </Descriptions.Item>
        </Descriptions>

        {/* Audit Information */}
        <Descriptions
          title={t('wave.waveRule.detail.audit.title')}
          column={2}
          bordered
          style={{ marginTop: 24 }}
        >
          <Descriptions.Item label={t('wave.waveRule.detail.audit.creatorName')}>
            {detail.creatorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.audit.createTime')}>
            {formatDateTime(detail.gmtCreate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.audit.modifierName')}>
            {detail.modifierName}
          </Descriptions.Item>
          <Descriptions.Item label={t('wave.waveRule.detail.audit.modifiedTime')}>
            {formatDateTime(detail.gmtModified)}
          </Descriptions.Item>
        </Descriptions>
      </Spin>
    </Drawer>
  );
};

export default WaveRuleDetail;
